import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

export default function AddTradeDialog({ open, onOpenChange, onSaved }) {
  const { activeId } = useAccounts();
  const [f, setF] = useState({
    symbol: "", direction: "long", quantity: "1", entry_price: "", exit_price: "",
    entry_time: new Date().toISOString().slice(0,10), fees: "0", notes: "",
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => setTags(tags.filter(x => x !== t));

  const submit = async () => {
    if (!activeId) return toast.error("No account selected");
    if (!f.symbol || !f.entry_price || !f.exit_price) return toast.error("Fill required fields");
    setLoading(true);
    try {
      await api.post("/trades", {
        account_id: activeId,
        symbol: f.symbol.toUpperCase(),
        direction: f.direction,
        quantity: parseFloat(f.quantity),
        entry_price: parseFloat(f.entry_price),
        exit_price: parseFloat(f.exit_price),
        entry_time: new Date(f.entry_time).toISOString(),
        fees: parseFloat(f.fees || 0),
        notes: f.notes,
        tags,
      });
      toast.success("Trade added");
      onSaved?.();
      onOpenChange(false);
      setF({ ...f, symbol: "", entry_price: "", exit_price: "", notes: "" });
      setTags([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg text-foreground">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Trade</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Symbol *</Label>
            <Input value={f.symbol} onChange={e => setF({...f, symbol: e.target.value})} placeholder="RELIANCE, BTC-USD…" data-testid="trade-symbol" />
          </div>
          <div>
            <Label>Direction</Label>
            <Select value={f.direction} onValueChange={v => setF({...f, direction: v})}>
              <SelectTrigger data-testid="trade-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="long">Long</SelectItem>
                <SelectItem value="short">Short</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quantity</Label>
            <Input type="number" value={f.quantity} onChange={e => setF({...f, quantity: e.target.value})} data-testid="trade-qty" />
          </div>
          <div>
            <Label>Entry price *</Label>
            <Input type="number" value={f.entry_price} onChange={e => setF({...f, entry_price: e.target.value})} data-testid="trade-entry" />
          </div>
          <div>
            <Label>Exit price *</Label>
            <Input type="number" value={f.exit_price} onChange={e => setF({...f, exit_price: e.target.value})} data-testid="trade-exit" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={f.entry_time} onChange={e => setF({...f, entry_time: e.target.value})} data-testid="trade-date" />
          </div>
          <div>
            <Label>Fees</Label>
            <Input type="number" value={f.fees} onChange={e => setF({...f, fees: e.target.value})} data-testid="trade-fees" />
          </div>
          <div className="col-span-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2 min-h-[24px] mt-2">
              {tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                  {t}
                  <button type="button" onClick={() => removeTag(t)}><X size={10} /></button>
                </span>
              ))}
            </div>
            <Input
              placeholder="Type a tag and press Enter (e.g. breakout, scalp, fomo)"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              data-testid="trade-tag-input"
            />
          </div>
          <div className="col-span-2">
            <Label>Notes &amp; trade logic</Label>
            <Textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} placeholder="Setup, thesis, what went right/wrong…" data-testid="trade-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-full" data-testid="trade-save-btn">
            {loading ? "Saving…" : "Save trade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
